import { useCallback, useEffect, useRef, useState } from "react";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { HOMEIQ_LOGO, VASTU_BY_HOMEIQ_LOGO, VASTU_LOGO } from "@/assets/brand";

const TABS = [
    { id: "home", label: "Home" },
    { id: "affordability", label: "Affordability" },
    { id: "market", label: "Market" },
    { id: "investment", label: "Investment" },
    { id: "loan", label: "Loan" },
    { id: "vastu", label: "Vastu" },
];

export default function Header() {
    const { activeSection, setActiveSection } = useHomeIQ();
    const inVastu = activeSection === "vastu";
    const secondaryLogo = inVastu ? VASTU_LOGO : VASTU_BY_HOMEIQ_LOGO;
    const secondaryAlt = inVastu ? "Vastu" : "Vastu by HomeIQ";

    const railRef = useRef(null);
    const [edges, setEdges] = useState({ atStart: true, atEnd: false });

    // Match the landing Navbar's edge-fade behaviour: on mobile the six
    // tabs live in a horizontal rail; fades signal that more tabs exist
    // beyond the visible slice and vanish when the user reaches an end.
    const measureEdges = useCallback(() => {
        const el = railRef.current;
        if (!el) return;
        const atStart = el.scrollLeft <= 2;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
        setEdges({ atStart, atEnd });
    }, []);

    useEffect(() => {
        measureEdges();
        const el = railRef.current;
        if (!el) return undefined;
        el.addEventListener("scroll", measureEdges, { passive: true });
        window.addEventListener("resize", measureEdges);
        return () => {
            el.removeEventListener("scroll", measureEdges);
            window.removeEventListener("resize", measureEdges);
        };
    }, [measureEdges]);

    // When the active tab changes, scroll it into view inside the rail so
    // users always see the highlighted tab even after tapping one that
    // was near an edge.
    useEffect(() => {
        const el = railRef.current;
        if (!el) return;
        const btn = el.querySelector(`[data-testid="tab-${activeSection}"]`);
        if (btn && typeof btn.scrollIntoView === "function") {
            btn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        }
    }, [activeSection]);

    return (
        <header
            data-testid="shell-header"
            className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#0A1F44]/10"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-14 md:h-16 flex items-center justify-between gap-3 md:gap-6">
                <button
                    type="button"
                    onClick={() => setActiveSection("home")}
                    data-testid="header-logo"
                    className="flex items-center gap-3 shrink-0"
                >
                    <img src={HOMEIQ_LOGO} alt="HomeIQ" className="h-9 md:h-10 w-auto object-contain" />
                    <span aria-hidden className="hidden sm:block h-6 w-px bg-[#0A1F44]/15" />
                    <img
                        src={secondaryLogo}
                        alt={secondaryAlt}
                        data-testid={inVastu ? "header-logo-vastu-standalone" : "header-logo-vastu-by-homeiq"}
                        className={`hidden sm:block w-auto object-contain opacity-95 ${inVastu ? "h-10 md:h-12" : "h-12 md:h-14"}`}
                    />
                </button>

                <div className="relative flex-1 min-w-0">
                    <nav
                        ref={railRef}
                        aria-label="HomeIQ assessments"
                        data-testid="header-tab-rail"
                        className="flex items-center gap-1 md:gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {TABS.map((t) => {
                            const active = activeSection === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setActiveSection(t.id)}
                                    data-testid={`tab-${t.id}`}
                                    className={`px-3 md:px-4 py-2.5 md:py-1.5 rounded-full text-xs md:text-[13px] font-semibold whitespace-nowrap transition-colors min-h-[40px] md:min-h-0 flex items-center shrink-0 snap-start ${
                                        active
                                            ? "bg-[#0A1F44] text-white"
                                            : "text-[#0A1F44]/65 hover:text-[#0A1F44] hover:bg-[#0A1F44]/5"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </nav>
                    <div
                        aria-hidden="true"
                        data-testid="header-rail-fade-left"
                        className={`md:hidden pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent transition-opacity duration-200 ${
                            edges.atStart ? "opacity-0" : "opacity-100"
                        }`}
                    />
                    <div
                        aria-hidden="true"
                        data-testid="header-rail-fade-right"
                        className={`md:hidden pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent transition-opacity duration-200 ${
                            edges.atEnd ? "opacity-0" : "opacity-100"
                        }`}
                    />
                </div>
            </div>
        </header>
    );
}
